"use client";

import classNames from "classnames";
import { RefObject, useCallback, useEffect, useRef, useState, type JSX } from "react";
import { TranslationScopes } from "@finbackoffice/enums";
import { DropdownOption, InputFieldError } from "@finbackoffice/site-core";
import { IModalForwardRefPropsV2 } from "contexts";
import Button from "../button/Button";
import Loading from "../loading/Loading";
import Modal from "../modal/Modal";
import { ModalStyleTypeEnum } from "../modal/constants/modal-config";
import Translate from "../translate/Translate";
import { Svg } from "../svg/Svg";
import List from "./List";
import styles from "./custom-select.module.sass";

export type IOnChangeValue<T extends string, P extends boolean> = P extends true ? T[] : T;

type ICustomSelectProps<T extends string, P extends boolean = false> = {
    variant?: string;
    selected: P extends true ? T[] : T;
    data: DropdownOption[];
    wrapperClassname?: string;
    onChange: (val: IOnChangeValue<T, P>) => void;
    loading?: boolean;
    placeholder?: string;
    type?: string;
    multiple?: P;
    renderItem?: (item: DropdownOption) => JSX.Element;
    disabled?: boolean;
    testId?: string;
    required?: boolean;
    error?: InputFieldError;
    svgSrc?: string;
    label?: string;
    variantStyle?: "default" | "gradient";
};

const CustomSelect = <T extends string, P extends boolean = false>({
    variant = "modal",
    selected,
    onChange,
    data,
    type,
    placeholder,
    wrapperClassname,
    multiple,
    loading,
    renderItem,
    error,
    required,
    disabled,
    label,
    testId = "",
    svgSrc = "",
    variantStyle = "default",
}: ICustomSelectProps<T, P>) => {
    const modalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const [opened, setOpened] = useState<boolean>(false);
    const dropdownRef: RefObject<HTMLDivElement | null> = useRef(null);

    useEffect(() => {
        const outsideClickHandler = (event: any) => {
            if (dropdownRef && !dropdownRef.current?.contains(event.target)) {
                setOpened(false);
            }
        };

        if (variant === "dropdown") {
            if (opened) {
                document.addEventListener("mousedown", outsideClickHandler);
            } else {
                document.removeEventListener("mousedown", outsideClickHandler);
            }
        }
        return () => {
            if (variant === "dropdown") {
                document.removeEventListener("mousedown", outsideClickHandler);
            }
        };
    }, [opened, variant]);

    const onClickHandler = (value: string) => {
        if (multiple) {
            onChange([value] as IOnChangeValue<T, P>);
        } else {
            onChange(value as IOnChangeValue<T, P>);
        }

        if (variant === "modal") {
            void modalRef.current?.close();
        } else if (variant === "dropdown") {
            setOpened(false);
        }
    };

    const onCheckboxlClick = (val: string) => {
        const index: number = Array.isArray(selected) ? selected?.indexOf(val as T) : -1;
        if (index !== -1) {
            const tempSelected: string[] = [...selected];
            tempSelected.splice(index, 1);

            onChange(tempSelected as IOnChangeValue<T, P>);
        } else if (selected[0]) {
            onChange([...selected, val] as IOnChangeValue<T, P>);
        } else {
            onChange([val] as IOnChangeValue<T, P>);
        }
    };

    const open = useCallback(() => {
        if (variant === "modal") {
            modalRef.current?.open();
        } else {
            setOpened(!opened);
        }
    }, [opened, variant]);

    const handleOpen = useCallback(() => {
        if (!loading) {
            open();
        }
    }, [loading, open]);

    const selectedValue = data?.find((item) =>
        multiple ? item.value === selected[0] : item.value === selected,
    );

    return (
        <>
            <div
                className={classNames(wrapperClassname, styles.container)}
                ref={dropdownRef}
                data-testid={testId}>
                {label && <label className={styles.label}>{label}:</label>}
                <div className={classNames(required && "required", error && styles.fieldError)}>
                    <Button
                        type="button"
                        variant="custom"
                        className={classNames(
                            styles.multipleSelector,
                            variantStyle && styles[variantStyle],
                            error && styles.fieldError,
                        )}
                        onClick={handleOpen}
                        disabled={disabled}>
                        {loading ? (
                            <Loading wrapperClassName={styles.dropdownLoader} />
                        ) : selectedValue ? (
                            <>
                                <span>
                                    {selectedValue.iconClass && (
                                        <i className={selectedValue.iconClass} />
                                    )}
                                    {(selectedValue.svgSrc || svgSrc) && (
                                        <Svg
                                            src={selectedValue.svgSrc || svgSrc}
                                            className="svg-icon"
                                        />
                                    )}
                                    <span className={styles.value}>
                                        <Translate
                                            tid={
                                                typeof selectedValue.label === "string"
                                                    ? selectedValue.label
                                                    : selectedValue.label.tid
                                            }
                                            namespace={
                                                typeof selectedValue.label !== "string"
                                                    ? selectedValue.label.namespace
                                                    : TranslationScopes.Common
                                            }
                                        />
                                    </span>
                                </span>
                                {multiple && selected.length > 1 && (
                                    <i>{`+${selected.length - 1}`}</i>
                                )}
                            </>
                        ) : (
                            <span className={styles.placeholder}>
                                {placeholder ? (
                                    placeholder
                                ) : (
                                    <Translate tid="base_dropdown_placeholder" />
                                )}
                            </span>
                        )}
                    </Button>
                </div>
                {error && <span className={styles.error}>{error.message}</span>}
                {variant === "dropdown" && opened && (
                    <div className={styles.dropdown}>
                        <List
                            data={data}
                            selected={selected}
                            onClickHandler={onClickHandler}
                            onCheckboxlClick={onCheckboxlClick}
                            multiple={multiple || false}
                            renderItem={renderItem}
                        />
                    </div>
                )}
            </div>
            {variant === "modal" && type && (
                <Modal
                    ref={modalRef}
                    type={type}
                    animateVariant="opacity"
                    styleConfigType={ModalStyleTypeEnum.CustomDropdown}
                    styleClass={styles.dropdownModal}
                    priority>
                    <List
                        data={data}
                        selected={selected}
                        onClickHandler={onClickHandler}
                        onCheckboxlClick={onCheckboxlClick}
                        multiple={multiple || false}
                        renderItem={renderItem}
                    />
                </Modal>
            )}
        </>
    );
};

export default CustomSelect;
